import React, { useEffect, useState } from "react";
import { ErrorMessage, Field, useFormikContext, useField } from "formik";
import { useTranslation } from "react-i18next";
import { Col } from "react-bootstrap";
import { TooltipCustom } from "../index";
import { handlePaste } from "@me-team/host/src/utils/utils";

interface TextInputProps {
    name: string;
    placeholder?: string;
    label: React.ReactNode;
    maxLength?: number;
    labelCol?: number;
    inputCol?: number;
    textTooltip?: string;
    id: string;
    disabled?: boolean;
    required?: boolean;
    infoText?: string;
    isLabel?: boolean;
    inputColLg?: number;
    inputColSm?: number;
    showCounter?: boolean;
    counterClassName?: string;
    autoComplete?: React.InputHTMLAttributes<HTMLInputElement>["autoComplete"];
    rightSlot?: React.ReactNode;
}

const TextInput: React.FC<TextInputProps> = ({
    name,
    placeholder,
    label,
    maxLength,
    labelCol = 2,
    inputCol = 10,
    inputColSm = 9,
    textTooltip,
    id,
    disabled,
    required,
    infoText,
    isLabel = true,
    inputColLg,
    showCounter = false,
    counterClassName,
    autoComplete,
    rightSlot,
}) => {
    const { t } = useTranslation();
    const { handleBlur } = useFormikContext();
    const [field, meta] = useField<string | null>(name);
    const [tooltipPlacement, setTooltipPlacement] = useState<"top" | "right">("top");
    const valueAsString = typeof field.value === "string" ? field.value : "";
    const currentLength = valueAsString.length;
    const shouldShowCounter = Boolean(showCounter && typeof maxLength === "number");

    useEffect(() => {
        const handleResize = () => {
            setTooltipPlacement(window.innerWidth < 1025 ? "top" : "right");
        };

        handleResize();
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    return (
        <>
            {isLabel ? (
                <Col sm={3} md={labelCol} className="mb-2 mb-sm-0 text-start">
                    <label
                        htmlFor={id}
                        className="h-100 d-flex align-items-center max-height-field text-dark fs-7 fw-bold pt-md-3"
                    >
                        <span className={`${required ? "required-label" : ""}`}>{label}</span>
                    </label>
                </Col>
            ) : null}
            <Col sm={inputColSm} md={inputCol} lg={inputColLg} className="text-start">
                <div className="position-relative">
                    <Field
                        name={name}
                        id={id}
                        placeholder={t(placeholder)}
                        className={`form-control text-dark fs-7 ${meta.touched && meta.error ? "is-invalid" : ""}${
                            rightSlot ? " padding-placeholder-is-invalid padding-background-position-is-invalid" : ""
                        }`}
                        onBlur={handleBlur}
                        maxLength={maxLength}
                        disabled={disabled}
                        autoComplete={autoComplete}
                        onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => handlePaste(e, maxLength)}
                    />
                    {rightSlot ? (
                        <div
                            className={`position-absolute top-0 translate-middle-y mt-4 d-flex align-items-center ${
                                textTooltip ? "end-0 me-7" : "end-0 me-2"
                            }`}
                        >
                            {rightSlot}
                        </div>
                    ) : null}
                    {infoText ? (
                        <div className="mt-1">
                            <span className="fs-7 text-grey">{t(infoText)}</span>
                        </div>
                    ) : null}

                    <ErrorMessage name={name}>
                        {(msg) => <div className="invalid-feedback fs-7 d-block">{msg}</div>}
                    </ErrorMessage>

                    {shouldShowCounter ? (
                        <div>
                            <span className={`fs-7 text-grey ${counterClassName ?? ""}`}>
                                {currentLength}/{maxLength}
                            </span>
                        </div>
                    ) : null}

                    {textTooltip && (
                        <TooltipCustom placement={tooltipPlacement} text={textTooltip}>
                            <i
                                className={`bi bi-info-circle position-absolute top-0 end-0 translate-middle-y text-dark mt-4 me-4
               ${meta.error ? "d-none" : ""}`}
                            ></i>
                        </TooltipCustom>
                    )}
                </div>
            </Col>
        </>
    );
};

export default TextInput;
